/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\AiAgentInteractionTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/

import React from 'react';
import { Radio } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { useI18n } from '../../../hooks/useI18n';

interface InteractionFormData {
  barge_in_enabled: boolean;
  barge_in_sensitivity: number;
  barge_in_min_speech_ms: number;
  greeting_protection_ms: number;
  idle_timeout_ms: number;
  idle_retries: number;
  allow_language_change: boolean;
  transcription_enabled: boolean;
  store_transcripts: boolean;
  live_transcript: boolean;
  record_call: boolean;
}

interface AiAgentInteractionTabProps {
  formData: InteractionFormData;
  onChange: (field: keyof InteractionFormData, value: unknown) => void;
}

export function AiAgentInteractionTab({ formData, onChange }: AiAgentInteractionTabProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('aiAgents.tabs.interaction') || 'Interaction'} icon={<Radio className="h-5 w-5" />} className="mt-6">
      <h3 className="text-sm font-medium text-foreground mb-4">{t('aiAgents.form.conversationControl') || 'Conversation Control'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
        <SimpleTooltip content={t('aiAgents.form.enableBargeInTooltip')} position="top">
          <Label className="text-left">{t('aiAgents.form.enableBargeIn') || 'Enable Barge-In'}</Label>
        </SimpleTooltip>
        <Toggle value={formData.barge_in_enabled} onChange={(v) => onChange('barge_in_enabled', v)} />

        <SimpleTooltip content={t('aiAgents.form.greetingProtectionMsTooltip') || 'Duration to ignore caller audio at call start. Set to 0 to disable.'} position="top">
          <Label className="text-left">{t('aiAgents.form.greetingProtectionMs') || 'Greeting Protection (ms)'}</Label>
        </SimpleTooltip>
        <input type="number" value={formData.greeting_protection_ms}
          onChange={e => onChange('greeting_protection_ms', parseInt(e.target.value) || 0)}
          className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
          min={0} max={30000} />

        {formData.barge_in_enabled && (
          <>
            <SimpleTooltip content={t('aiAgents.form.sensitivityTooltip')} position="top">
              <Label className="text-left">{t('aiAgents.form.sensitivity') || 'Sensitivity'}: {formData.barge_in_sensitivity}</Label>
            </SimpleTooltip>
            <input type="range" value={formData.barge_in_sensitivity}
              onChange={e => onChange('barge_in_sensitivity', parseFloat(e.target.value))}
              className="accent-primary" min={0} max={1} step={0.1} />
            <SimpleTooltip content={t('aiAgents.form.minSpeechMsTooltip')} position="top">
              <Label className="text-left">{t('aiAgents.form.minSpeechMs') || 'Min Speech (ms)'}</Label>
            </SimpleTooltip>
            <input type="number" value={formData.barge_in_min_speech_ms}
              onChange={e => onChange('barge_in_min_speech_ms', parseInt(e.target.value) || 200)}
              className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
              min={50} max={2000} />
          </>
        )}

        <SimpleTooltip content={t('aiAgents.form.idleTimeoutMsTooltip') || 'Time in ms to wait for user response before prompting. 0 to disable.'} position="top">
          <Label className="text-left">{t('aiAgents.form.idleTimeoutMs') || 'Idle Timeout (ms)'}</Label>
        </SimpleTooltip>
        <input type="number" value={formData.idle_timeout_ms}
          onChange={e => onChange('idle_timeout_ms', parseInt(e.target.value) || 0)}
          className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
          min={0} max={60000} step={1000} />

        <SimpleTooltip content={t('aiAgents.form.idleRetriesTooltip') || 'Number of times to prompt the user before taking action. 0 to act immediately.'} position="top">
          <Label className="text-left">{t('aiAgents.form.idleRetries') || 'Idle Retries'}</Label>
        </SimpleTooltip>
        <input type="number" value={formData.idle_retries}
          onChange={e => onChange('idle_retries', parseInt(e.target.value) || 0)}
          className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
          min={0} max={10} />

        <SimpleTooltip content={t('aiAgents.form.allowLanguageChangeTooltip') || 'Allow the AI to adapt to the caller s language.'} position="top">
          <Label className="text-left">{t('aiAgents.form.allowLanguageChange') || 'Allow Language Switching'}</Label>
        </SimpleTooltip>
        <Toggle value={formData.allow_language_change} onChange={(v) => onChange('allow_language_change', v)} />
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-medium text-foreground mb-4">{t('aiAgents.form.transcription') || 'Transcription'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
          <SimpleTooltip content={t('aiAgents.form.enableTranscriptionTooltip')} position="top">
            <Label className="text-left">{t('aiAgents.form.enableTranscription') || 'Enable Transcription'}</Label>
          </SimpleTooltip>
          <Toggle value={formData.transcription_enabled} onChange={(v) => onChange('transcription_enabled', v)} />

          <SimpleTooltip content={t('aiAgents.form.storeTranscriptsTooltip')} position="top">
            <Label className="text-left">{t('aiAgents.form.storeTranscripts') || 'Store Transcripts'}</Label>
          </SimpleTooltip>
          <Toggle value={formData.store_transcripts} onChange={(v) => onChange('store_transcripts', v)} />

          <SimpleTooltip content={t('aiAgents.form.liveTranscriptTooltip')} position="top">
            <Label className="text-left">{t('aiAgents.form.liveTranscript') || 'Live Transcript'}</Label>
          </SimpleTooltip>
          <Toggle value={formData.live_transcript} onChange={(v) => onChange('live_transcript', v)} />

          <SimpleTooltip content={t('aiAgents.form.recordCallTooltip') || 'Enable call recording for this AI Agent'} position="top">
            <Label className="text-left">{t('aiAgents.form.recordCall') || 'Record Call'}</Label>
          </SimpleTooltip>
          <Toggle value={formData.record_call} onChange={(v) => onChange('record_call', v)} />
        </div>
      </div>
    </FormBox>
  );
}
